import React from 'react';
import { Box, Button, Dialog, Stack, Typography } from '@material-ui/core';
import { PiWarning, PiCopy } from 'react-icons/pi';
import { toast } from 'react-toastify';
import String from './String';
import { useLanguage } from '../contexts/language';
import { useTheme } from '../contexts/theme';
import { getDeviceOsAndBrowser, isUnsupportedBrowser } from '../utils/utils';

// Chrome is preinstalled on Android, Safari on iOS: zero-friction suggestions.
const SUGGESTED_BROWSER = getDeviceOsAndBrowser().os === 'iOS' ? 'Safari' : 'Chrome';

const useCopyAddress = () => {
  const { content } = useLanguage();
  // full href, query included: a manual-code QR landing survives the browser switch
  return React.useCallback(async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success(content('address_copied'));
    } catch (error) {
      console.error('Could not copy address to clipboard', error);
      toast.error(content('address_copy_error'));
    }
  }, [content]);
};

/**
 * Compact variant for in-flow placement (e.g. the GPS setup step), covering the
 * user who dismissed the entry dialog and then got stuck. Renders nothing on
 * supported browsers, so callers can mount it unconditionally.
 */
export const UnsupportedBrowserHint = () => {
  const { content } = useLanguage();
  const { styles } = useTheme();
  const copyAddress = useCopyAddress();

  if (!isUnsupportedBrowser()) return null;

  return (
    <Stack spacing={1} alignItems="center" textAlign="center" pt={1}>
      <Typography fontWeight="bold" style={{ color: styles.palette.warning?.main ?? '#ED6C02' }}>
        <PiWarning aria-hidden="true" style={{ verticalAlign: 'text-bottom', marginRight: 4 }} />
        <String s="unsupported_browser_title" />
      </Typography>
      <Typography fontSize="0.9rem">
        <String s="unsupported_browser_description" />{' '}
        {content('unsupported_browser_open_with').replace('{browser}', SUGGESTED_BROWSER)}
      </Typography>
      <Button
        size="small"
        variant="outlined"
        color="secondary"
        startIcon={<PiCopy aria-hidden="true" />}
        onClick={copyAddress}
      >
        <String s="copy_address" />
      </Button>
    </Stack>
  );
};

/**
 * Entry disclaimer for unsupported browsers (see isUnsupportedBrowser). Mounted
 * at App level, outside SessionProvider, so it covers every entry route (login,
 * setup, redeem, manual-code landing). Shows once per app load, in-memory only:
 * core features are broken on these browsers, re-warning each visit is intended.
 */
const UnsupportedBrowserDialog = () => {
  const [open, setOpen] = React.useState(isUnsupportedBrowser);
  const { content } = useLanguage();
  const { styles } = useTheme();
  const copyAddress = useCopyAddress();

  if (!open) return null;

  return (
    <Dialog open aria-label={content('aria_unsupported_browser_dialog')}>
      <Box p={3} display="flex" flexDirection="column" alignItems="center" gap={2} textAlign="center">
        <PiWarning
          aria-hidden="true"
          style={{ fontSize: '4rem', color: styles.palette.warning?.main ?? '#ED6C02' }}
        />
        <Typography variant="h6" fontWeight="bold">
          <String s="unsupported_browser_title" />
        </Typography>
        <Typography>
          <String s="unsupported_browser_description" />
        </Typography>
        <Typography fontWeight="bold">
          {content('unsupported_browser_open_with').replace('{browser}', SUGGESTED_BROWSER)}
        </Typography>
        <Button fullWidth variant="contained" startIcon={<PiCopy aria-hidden="true" />} onClick={copyAddress}>
          <String s="copy_address" />
        </Button>
        <Button fullWidth variant="outlined" color="secondary" onClick={() => setOpen(false)}>
          <String s="understood_continue" />
        </Button>
      </Box>
    </Dialog>
  );
};

export default UnsupportedBrowserDialog;
